import { useEffect } from 'react';
import MobileApi from 'lib/MobileApi';

/**
 * Hook to observe Picture-in-Picture (PiP) status for the video in the provided container.
 *
 * Falls back to a MutationObserver if the video is not immediately available in the DOM,
 * since direct access to the video ref is not available.
 *
 * @param {React.RefObject<HTMLDivElement>} containerRef - Ref to the container that will contain the <video> element.
 */
export function usePiPObserver(containerRef) {
  useEffect(() => {
    const container = containerRef.current;
    if (
      typeof document === 'undefined'
      || !container
      || MobileApi.getIosBridge() === null
    ) {
      return undefined;
    }
    /**
     * On PiP change notify through app bridge
     */
    const handlePiPChange = () => {
      const isActive = !!document.pictureInPictureElement;
      MobileApi.pipStatus(isActive);
    };

    /**
     * Attach PiP event listeners to the first video element found in the container.
     * @returns {boolean} - True if a video was found and listeners were attached, false otherwise.
     */
    const attachListenersToVideo = () => {
      const video = container.querySelector('video');
      if (!video) return false;

      video.addEventListener('enterpictureinpicture', handlePiPChange);
      video.addEventListener('leavepictureinpicture', handlePiPChange);
      handlePiPChange();
      return true;
    };

    // Try to attach listeners on initial render
    // - If the video is not yet available, we will observe for changes in the container
    if (attachListenersToVideo()) return undefined;

    const observer = new MutationObserver(() => {
      if (attachListenersToVideo()) {
        observer.disconnect();
      }
    });

    observer.observe(container, { childList: true, subtree: true });

    return () => {
      observer.disconnect();
    };
  }, [containerRef]);
}
